/**
 * SIG-P1 — position direction has THREE states, and a binary test invents one.
 *
 * The backend sends `side = position.side or "unknown"` (positions.py) for
 * legacy or malformed rows — a deliberate, honest fail-soft. The table then
 * threw that honesty away with binary tests:
 *
 *     const isLong = trade.direction === "long";
 *     {isLong ? "↗ Long" : "↘ Short"}
 *
 * Anything not exactly "long" — including the literal string "unknown" —
 * rendered as a SHORT POSITION. And one site inverted it the other way,
 * defaulting an unknown to "long". So the same missing value was displayed as
 * a confident long in one place and a confident short in another.
 *
 * Telling a trader they are short when we do not know their side is worse than
 * telling them nothing: it is a directional claim they may act on, and acting
 * on a wrong side doubles exposure rather than closing it.
 *
 * `stat-card-row.tsx` already handled this correctly with three explicit
 * branches (long / short / "Mix unavailable"). This is that logic, single-
 * sourced, so the table cannot drift from it again.
 */

export type PositionDirection = "long" | "short" | "unknown";

/**
 * Normalize a raw direction value without guessing.
 *
 * Only the two exact known sides count. Everything else — "unknown", "flat",
 * null, undefined, an empty string, junk — is UNKNOWN, and callers must render
 * it as such rather than picking a side.
 */
export function readDirection(raw: unknown): PositionDirection {
  if (typeof raw !== "string") return "unknown";
  const normalized = raw.trim().toLowerCase();
  if (normalized === "long") return "long";
  if (normalized === "short") return "short";
  return "unknown";
}

/** True only for a direction we actually know. Use to gate directional UI. */
export function isKnownDirection(direction: PositionDirection): boolean {
  return direction !== "unknown";
}

/** Display label. Unknown gets an em-dash — never a side. */
export function directionLabel(
  direction: PositionDirection,
  variant: "arrow" | "caps" = "arrow",
): string {
  if (direction === "unknown") return "—";
  if (variant === "caps") return direction === "long" ? "LONG" : "SHORT";
  return direction === "long" ? "↗ Long" : "↘ Short";
}

/**
 * Tone for colouring. Unknown is deliberately neutral: green and red both
 * assert a side, and this is the case where we have none.
 */
export function directionTone(
  direction: PositionDirection,
): "up" | "down" | "neutral" {
  if (direction === "long") return "up";
  if (direction === "short") return "down";
  return "neutral";
}
